import { supabase } from '@/lib/supabaseClient';
import { updateJob, formatUsd, parseContractorDate } from '@/lib/jobsData';

export { formatUsd };

/* ----------------------------------------------------------------------------
 * Templates
 * ------------------------------------------------------------------------- */
export const REMINDER_TYPES = ['friendly', 'firm', 'final'];

export const REMINDER_LABEL = {
  friendly: 'Friendly',
  firm: 'Firm',
  final: 'Final Notice',
};

export const REMINDER_TONE = {
  friendly: 'default',
  firm: 'warning',
  final: 'danger',
};

const pickName = (client) => {
  if (!client) return 'there';
  // Heuristic: take first token before " and "/comma
  const trimmed = String(client).trim();
  const seg = trimmed.split(/(?:\s+and\s+|,)/i)[0];
  return seg || trimmed;
};

export const buildReminderMessage = (type, ctx = {}) => {
  const {
    client = '',
    jobName = 'your project',
    invoiceDesc = 'an invoice',
    amount = 0,
    due = '',
    daysPastDue = 0,
    // Neutral fallback — callers pass the tenant's company name; a real
    // company must never be the default for every tenant's emails.
    fromName = '',
  } = ctx;
  const greet = `Hi ${pickName(client)},`;
  const closing = fromName ? `Thanks,\n${fromName}` : 'Thanks,';

  if (type === 'friendly') {
    return [
      greet,
      ``,
      `Just a quick reminder that ${invoiceDesc} for ${jobName} (${formatUsd(amount)}) is due ${due ? `on ${due}` : 'soon'}. If you have any questions, just reply and I'll take care of you.`,
      ``,
      closing,
    ].join('\n');
  }
  if (type === 'firm') {
    return [
      greet,
      ``,
      `Following up on ${invoiceDesc} for ${jobName} totalling ${formatUsd(amount)}, originally due ${due || 'recently'}${daysPastDue > 0 ? ` (now ${daysPastDue} days past due)` : ''}. Please send payment as soon as possible so we can keep your project moving on schedule.`,
      ``,
      `If you've already paid, please disregard and let me know when it cleared so I can update your account.`,
      ``,
      closing,
    ].join('\n');
  }
  // final
  return [
    greet,
    ``,
    `This is a FINAL NOTICE on ${invoiceDesc} for ${jobName} totalling ${formatUsd(amount)}, originally due ${due || 'previously'}${daysPastDue > 0 ? ` and now ${daysPastDue} days past due` : ''}.`,
    ``,
    `If payment is not received within 7 days, this account will be referred to collections and a mechanic\u2019s lien may be filed against the property. Please reach out today so we can resolve this together.`,
    ``,
    closing,
  ].join('\n');
};

/**
 * Suggest the next template based on prior reminder count.
 */
export const suggestReminderType = (priorReminders = []) => {
  const arr = Array.isArray(priorReminders) ? priorReminders : [];
  if (arr.length === 0) return 'friendly';
  if (arr.length === 1) return 'firm';
  return 'final';
};

/* ----------------------------------------------------------------------------
 * Fetch invoices that need reminders
 * ------------------------------------------------------------------------- */
const JOB_FIELDS =
  'id,name,client,client_id,address,invoices,created_at';

export const fetchRemindersData = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_FIELDS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Returns one row per due/overdue invoice across all jobs.
 */
export const buildReminderQueue = (jobs = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const rows = [];
  for (const job of jobs) {
    const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
    invoices.forEach((inv, idx) => {
      if (inv?.status === 'paid') return;
      const due = parseContractorDate(inv?.due);
      let daysPastDue = 0;
      let daysUntilDue = null;
      let derivedStatus = inv?.status || 'upcoming';
      if (due) {
        const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));
        if (diff < 0) {
          derivedStatus = 'overdue';
          daysPastDue = Math.abs(diff);
        } else {
          daysUntilDue = diff;
          derivedStatus = diff <= 7 ? 'due' : 'upcoming';
        }
      }
      // Only show 'due' (within 7 days) or 'overdue' invoices in the queue.
      if (derivedStatus !== 'due' && derivedStatus !== 'overdue') return;
      const reminders = Array.isArray(inv?.reminders_sent) ? inv.reminders_sent : [];
      const lastReminder = reminders[reminders.length - 1] || null;
      rows.push({
        row_key: `${job.id}-${inv?.id || 'noid'}-${idx}`,
        invoice_id: inv?.id || null,
        idx,
        job_id: job.id,
        job_name: job.name || 'Untitled job',
        client: job.client || '',
        client_id: job.client_id || null,
        invoice_desc: inv?.desc || 'Invoice',
        amount: Number(inv?.amount) || 0,
        due: inv?.due || '',
        due_date: due,
        derived_status: derivedStatus,
        days_past_due: daysPastDue,
        days_until_due: daysUntilDue,
        reminders_sent: reminders,
        last_reminder: lastReminder,
        suggested_type: suggestReminderType(reminders),
      });
    });
  }
  // Sort: most past-due first, then due-soonest
  rows.sort((a, b) => {
    if (a.derived_status === 'overdue' && b.derived_status !== 'overdue') return -1;
    if (b.derived_status === 'overdue' && a.derived_status !== 'overdue') return 1;
    return (b.days_past_due || 0) - (a.days_past_due || 0);
  });
  return rows;
};

export const computeReminderStats = (rows = []) => {
  let outstanding = 0;
  let overdueAmt = 0;
  let overdueCount = 0;
  let dueCount = 0;
  let totalReminders = 0;
  for (const r of rows) {
    outstanding += r.amount;
    if (r.derived_status === 'overdue') {
      overdueAmt += r.amount;
      overdueCount += 1;
    } else if (r.derived_status === 'due') {
      dueCount += 1;
    }
    totalReminders += (r.reminders_sent || []).length;
  }
  return {
    count: rows.length,
    outstanding,
    overdueAmt,
    overdueCount,
    dueCount,
    totalReminders,
  };
};

export const applyReminderFilter = (
  rows = [],
  { search = '', status = 'all' } = {}
) => {
  const q = (search || '').trim().toLowerCase();
  return rows.filter((r) => {
    if (status === 'overdue' && r.derived_status !== 'overdue') return false;
    if (status === 'due' && r.derived_status !== 'due') return false;
    if (status === 'unsent' && (r.reminders_sent || []).length > 0) return false;
    if (status === 'sent' && (r.reminders_sent || []).length === 0) return false;
    if (!q) return true;
    const hay = [r.job_name, r.client, r.invoice_desc]
      .map((v) => (v || '').toLowerCase())
      .join(' | ');
    return hay.includes(q);
  });
};

/* ----------------------------------------------------------------------------
 * Writes — record reminder + mark paid
 * ------------------------------------------------------------------------- */
const genId = (prefix) => {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().split('-')[0].toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}-${id}`;
};

export const logReminderSent = async (job, invoiceId, type) => {
  if (!job?.id) throw new Error('Missing job id');
  if (!REMINDER_TYPES.includes(type)) throw new Error('Invalid reminder type');
  const entry = {
    id: genId('REM'),
    type,
    sent_at: new Date().toISOString(),
  };
  const invoices = (Array.isArray(job.invoices) ? job.invoices : []).map((inv) => {
    if (inv?.id !== invoiceId) return inv;
    const arr = Array.isArray(inv?.reminders_sent) ? inv.reminders_sent : [];
    return { ...inv, reminders_sent: [...arr, entry] };
  });
  return updateJob(job.id, { invoices });
};

export const markInvoicePaidFromReminder = async (job, invoiceId) => {
  if (!job?.id) throw new Error('Missing job id');
  const invoices = (Array.isArray(job.invoices) ? job.invoices : []).map((inv) =>
    inv?.id === invoiceId ? { ...inv, status: 'paid', paid_at: new Date().toISOString() } : inv
  );
  return updateJob(job.id, { invoices });
};

/* ----------------------------------------------------------------------------
 * Clipboard helper
 * ------------------------------------------------------------------------- */
export const copyToClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

/**
 * Aggregate ALL reminder history rows across jobs.
 */
export const buildReminderHistory = (jobs = []) => {
  const rows = [];
  for (const job of jobs) {
    const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
    for (const inv of invoices) {
      const reminders = Array.isArray(inv?.reminders_sent) ? inv.reminders_sent : [];
      reminders.forEach((rem, idx) => {
        rows.push({
          id: rem.id || `${job.id}-${inv?.id}-${idx}`,
          job_id: job.id,
          job_name: job.name || 'Untitled job',
          client: job.client || '',
          invoice_id: inv?.id || null,
          invoice_desc: inv?.desc || 'Invoice',
          amount: Number(inv?.amount) || 0,
          due: inv?.due || '',
          type: rem.type,
          sent_at: rem.sent_at,
        });
      });
    }
  }
  rows.sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
  return rows;
};
